import { Users, UtensilsCrossed, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { formatMinutes, hasTime } from '../utils/recipeTime';

export function InvitationScreen({ 
  inviteType = 'household', // 'household' or 'recipe'
  sharedBy,
  householdName,
  recipe, // { id, name, image, prepTime, cookTime, servings }
  onAccept,
  onDecline,
  loading = false,
}) {
  const [declined, setDeclined] = useState(false);
  
  const isHouseholdInvite = inviteType === 'household';
  const ownerFirstName = sharedBy ? sharedBy.trim().split(/\s+/)[0] : '';
  
  const handleDecline = () => {
    setDeclined(true);
    if (onDecline) {
      // Call after a brief delay to show the message
      setTimeout(() => onDecline(), 2000);
    }
  };

  // Show decline confirmation message
  if (declined) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ backgroundColor: '#F8F7F5' }}>
        <div className="w-full max-w-md text-center">
          <div className="mb-6">
            <CheckCircle 
              size={64} 
              style={{ color: '#AFBB8B' }}
              className="mx-auto"
            />
          </div>
          <h1 className="text-2xl mb-3" style={{ color: '#6B7A54' }}>
            Thanks for letting us know
          </h1>
          <p className="text-base leading-relaxed mb-3" style={{ color: '#5A5A5A' }}>
            {isHouseholdInvite
              ? `You have not been added to ${sharedBy ? `${sharedBy}'s` : 'the'} household.`
              : `You have not joined ${sharedBy ? `${sharedBy}'s` : 'this'} household, so you will not be able to view this recipe.`}
          </p>
          <p className="text-base leading-relaxed mb-6" style={{ color: '#5A5A5A' }}>
            {`You can still join later from the invitation link or by asking ${sharedBy ? sharedBy : 'the household owner'} to resend the invite.`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col p-6 pb-32" style={{ backgroundColor: '#F8F7F5' }}>
      {/* Header Icon */}
      <div className="flex justify-center mt-8 mb-6">
        <div 
          className="w-20 h-20 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#AFBB8B20' }}
        >
          {isHouseholdInvite ? (
            <Users size={40} style={{ color: '#AFBB8B' }} />
          ) : (
            <UtensilsCrossed size={40} style={{ color: '#AFBB8B' }} />
          )}
        </div>
      </div>

      {/* Heading */}
      <h1 className="text-2xl text-center mb-3" style={{ color: '#6B7A54' }}>
        {isHouseholdInvite ? (
          ownerFirstName ? `${ownerFirstName} invited you!` : 'You\'re invited!'
        ) : (
          sharedBy ? `${sharedBy} shared a recipe from their household` : 'Open this shared recipe'
        )}
      </h1>

      {/* Message */}
      <p className="text-base text-center leading-relaxed mb-8" style={{ color: '#5A5A5A' }}>
        {isHouseholdInvite ? (
          <>
            {ownerFirstName
              ? <>
                  Join {ownerFirstName}&apos;s household on <span className="font-semibold">MealPlan Genie</span> to plan meals, view recipes, and share the shopping list together.
                </>
              : (householdName
                  ? <>
                      Join {householdName} on <span className="font-semibold">MealPlan Genie</span> to plan meals, view recipes, and share the shopping list together.
                    </>
                  : <>
                      Join this household on <span className="font-semibold">MealPlan Genie</span> to plan meals, view recipes, and share the shopping list together.
                    </>)}
          </>
        ) : (
          <>
            Sign in with the <span className="font-semibold">MealPlan Genie</span> account that belongs to {sharedBy ? `${sharedBy}'s` : 'this'} household to view{' '}
            <strong>{recipe?.name || 'this recipe'}</strong>.
          </>
        )}
      </p>

      {/* Recipe Preview (if recipe invite) */}
      {!isHouseholdInvite && recipe && (
        <div className="mb-8">
          <div 
            className="rounded-2xl overflow-hidden shadow-sm"
            style={{ 
              backgroundColor: 'white',
              border: '1px solid #E5E5E5'
            }}
          >
            {recipe.image && (
              <div className="aspect-video w-full overflow-hidden">
                <img 
                  src={recipe.image} 
                  alt={recipe.name}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="p-4">
              <h3 className="text-lg mb-2" style={{ color: '#2C2C2C' }}>
                {recipe.name}
              </h3>
              {(hasTime(recipe.prepTime) || hasTime(recipe.cookTime) || hasTime(recipe.totalTime) || recipe.servings) && (
                <div className="flex gap-4 text-sm" style={{ color: '#8A8A8A' }}>
                  {hasTime(recipe.prepTime) && <span>Prep: {formatMinutes(recipe.prepTime)}</span>}
                  {hasTime(recipe.cookTime) && <span>Cook: {formatMinutes(recipe.cookTime)}</span>}
                  {!hasTime(recipe.prepTime) && !hasTime(recipe.cookTime) && hasTime(recipe.totalTime) && (
                    <span>Total: {formatMinutes(recipe.totalTime)}</span>
                  )}
                  {recipe.servings && <span>Serves: {recipe.servings}</span>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Benefits List */}
      <div className="mb-8">
        <h2 className="text-lg mb-4" style={{ color: '#6B7A54' }}>
          Household benefits
        </h2>
        <ul className="space-y-3">
          <>
            <li className="flex gap-3">
              <span style={{ color: '#AFBB8B' }}>✓</span>
              <span style={{ color: '#5A5A5A' }}>
                View the same meal plan and jump straight to the recipes your household is cooking
              </span>
            </li>
            <li className="flex gap-3">
              <span style={{ color: '#AFBB8B' }}>✓</span>
              <span style={{ color: '#5A5A5A' }}>
                Access shared shopping lists
              </span>
            </li>
            <li className="flex gap-3">
              <span style={{ color: '#AFBB8B' }}>✓</span>
              <span style={{ color: '#5A5A5A' }}>
                Browse the household recipe collection
              </span>
            </li>
           
          </>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="mt-auto space-y-3">
        <button
          onClick={onAccept}
          disabled={loading}
          className="w-full py-4 px-6 rounded-full text-base transition-opacity active:opacity-70 disabled:opacity-60"
          style={{ backgroundColor: '#AFBB8B', color: '#1F2814' }}
        >
          {loading ? 'Working...' : (isHouseholdInvite ? 'Join household' : 'Continue')}
        </button>
        <button
          onClick={handleDecline}
          disabled={loading}
          className="w-full py-4 px-6 rounded-full text-base transition-opacity active:opacity-70 disabled:opacity-60"
          style={{ 
            backgroundColor: 'white',
            color: '#5A5A5A',
            border: '1px solid #D0D0D0'
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
